import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos, { LogoObject } from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import StatsInfo, { StatsInfoProps } from "@components/new-ui/IndustriesSection/StatsInfo";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import { processEnv } from "@lib/processEnv";
import { IIndustries } from "@pages/industry/finance/whatsapp";
import { windowOpenNewTab } from "@utils/common";
import axios from "axios";
import { GetStaticProps } from "next";
import Image from "next/image";
import React from "react";
import { MdOutlineArrowForwardIos } from "react-icons/md";

const herosectionData: HerosectionProps = {
    chipTitle: "E-COMMERCE",
    title: "Effortless Ecommerce WhatsApp Integration to Grow Your Online Store",
    description: "From running automated campaigns to sending personalized product recommendations to post-purchase info, do it all with Gallabox’s nimble features.",
    primaryButtonText: "Try for Free",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/e-commerce-hero-image.png",
};

const statsInfoData: StatsInfoProps[] = [
    { percentage: "69%", label: "Increase in personalized outreach" },
    { percentage: "38%", label: "Increase in abandoned cart recovery rates" },
    { percentage: "78%", label: "Reduction customer support response time" },
];

const featureInfo1: FeatureInfo = {
    mainTitle: "Attract and acquire customers",
    subtitle: "By making it easy for prospects to enter your virtual store",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/recommendation-flowchat.png",
    featureInfoList: [
        "Turning Meta ads to WhatsApp chats",
        "Diverting QR code/widget enquiries to chats",
        "Capturing customer preferences",
        "Understanding prospect buying behavior",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Seamless sales conversions",
    subtitle: "Made possible by automating the entire purchase process",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/custom-settings.png",
    featureInfoList: [
        "Product showcase using catalogs",
        "Simplified checkout with quick purchase links",
        "Secure payments within WhatsApp chat",
        "Cross-selling with tailored product recommendations",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Streamlined order management",
    subtitle: "With timely notifications and instant updates",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/online-order.png",
    featureInfoList: [
        "Easy sharing of rich media for better decision-making",
        "Instant order confirmations and updates",
        "Real-time delivery tracking notifications",
        "Automated return and refund workflows",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Customer retention and loyalty",
    subtitle: "Using segmentation by buyer behaviour patterns",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/whatsapp-template-message.png",
    featureInfoList: [
        "Sharing of loyalty rewards and redemption offers",
        "Offering exclusive discounts for repeat customers",
        "Gathering post-purchase feedback",
        "Nurturing relationships with personalized messages",
    ],
};

const featureInfo5: FeatureInfo = {
    mainTitle: "Marketing and brand building",
    subtitle: "By running consistently engaging campaigns",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/template-message-body.png",
    featureInfoList: [
        "Targeted campaigns for specific audiences",
        "New launch announcements",
        "Driving seasonal sales with festive offers",
        "Boosting reviews/ratings with automated requests",
    ],
};

const ecommerceProductCardData: Array<Product> = [
    {
        title: "Click-to-WhatsApp ads",
        description: "Drive customer interest and engagement on WhatsApp directly from Meta ads.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/click-whatsapp-ads.png",
        externalLink: "https://gallabox.com/click-to-whatsapp-ads",
    },
    {
        title: "Broadcast",
        description: "Amplify your offers and deals in a few clicks ensuring maximum reach.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/broadcast.png",
        externalLink: "https://gallabox.com/whatsapp-broadcast",
    },
    {
        title: "Drip marketing",
        description: "Keep prospects’ interest alive with timely nudges throughout their buying journey.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/drip-marketing.png",
        externalLink: "https://gallabox.com/whatsapp-drip-marketing",
    },
    {
        title: "Flows",
        description: "Collect prospect/customer info for segmentation and personalized recommendations.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/whatsapp-flow.png",
        externalLink: "https://gallabox.com/whatsapp-flows",
    },
    {
        title: "Chatbot",
        description: "Answer FAQs and send abandoned cart nudges on WhatsApp with no-code AI bots.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/bot.png",
        externalLink: "https://gallabox.com/whatsapp-chatbot",
    },
    {
        title: "Shared team inbox",
        description: "Reduce response time rates and improve customer satisfaction with a single, AI-powered inbox.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/inbox.png",
        externalLink: "https://gallabox.com/whatsapp-shared-inbox",
    },
    {
        title: "AI rewrite",
        description: "Edit your responses with our built-in Gen AI and respond to prospects in multiple languages.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/gallabox-gen-ai.png",
        externalLink: "https://gallabox.com/whatsapp-ai-chatbot",
    },
    {
        title: "Payments",
        description: "Request product payments and send automated reminders to customers directly on WhatsApp.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/group.png",
        externalLink: "https://gallabox.com/whatsapp-payments",
    },
];

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/hugg.png",
        chiptitle: "E-COMMERCE",
        title: "Maximizing Conversions and Streamlining Support: Hugg's Transformation with Gallabox",
        statsInfoData: [
            { percentage: "37%", label: "Improvement in CTWA" },
            { percentage: "30%", label: "Increase in conversion rates" },
            { percentage: "20%", label: "Growth in number of orders" },
        ],
        url: "https://gallabox.com/case-study/hugg",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/sattvic-store.png",
        chiptitle: "E-COMMERCE",
        title: "Maximizing Conversions and Streamlining Support: Hugg's Transformation with Gallabox",
        statsInfoData: [
            { percentage: "37%", label: "Improvement in CTWA" },
            { percentage: "30%", label: "Increase in conversion rates" },
            { percentage: "20%", label: "Growth in number of orders" },
        ],
        url: "https://gallabox.com/case-study/sattvic-store",
    },
];

const clientLogosData: LogoObject[] = [
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/biotique.svg", alt: "biotique", width: 120, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/burgerman.svg", alt: "burgerman", width: 100, height: 48 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/designer-shaik.svg", alt: "designer-shaik", width: 100, height: 48 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/eter.svg", alt: "eter", width: 50, height: 48 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/glen.svg", alt: "glen", width: 120, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/hugg.svg", alt: "hugg", width: 100, height: 90 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/kumaren-silks.svg", alt: "kumaren-silks", width: 100, height: 38 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/lovegen.svg", alt: "lovegen", width: 100, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/lux.svg", alt: "lux", width: 100, height: 90 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/royaloak.svg", alt: "royaloak", width: 120, height: 31 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/saravana.svg", alt: "saravana", width: 120, height: 76 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/sedar.svg", alt: "sedar", width: 100, height: 51 },
    { url: "https://assets.gallabox.com/gb-home/industries/ecommerce/spaces.svg", alt: "space", width: 100, height: 51 },
];

const EcommerceLanding: React.FC<IIndustries> = ({ categoryResult }) => {
    return (
        <NewLayout
            description="Increase sales and automate customer chats with Gallabox’s ecommerce WhatsApp integration. Recover carts, send updates, and engage customers easily."
            title="Boost Your Online Sales with Ecommerce WhatsApp Integration"
            canonical="https://gallabox.com/industry/ecommerce/whatsapp"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/industries/ecommerce/ecommerce-og.png" }}
            schema={{
                heroOrOgImageAlt: "ecommerce-image",
                heroOrOgImageDescription: "Effortless Ecommerce WhatsApp Integration to Grow Your Online Store",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/industries/ecommerce/e-commerce-hero-image.png",
                softwareFeatureName: "ecommerce",
            }}
        >
            <Herosection herosection={herosectionData} />
            <StatsInfo statsInfo={statsInfoData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos logos={clientLogosData} />
            </section>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} />
            <FeatureInfoCard {...featureInfo5} />
            <ProductSection
                productCardData={ecommerceProductCardData}
                title="Gift your customers the ease of shopping"
                description="Sleek features to power your store using WhatsApp automation"
            />

            <section className={`${container_classname} py-20`}>
                <h2 className="md:text-6xl text-4xl mb-12" style={{ fontFamily: "Degular Display" }}>
                    Ready-to-test e-commerce chatbot templates
                </h2>
                <div className={"grid md:grid-cols-3 md:gap-5 gap-10"}>
                    {categoryResult.slice(0, 3).map((data: any) => (
                        <div
                            key={data.id}
                            className="border border-solid border-gray-200 rounded cursor-pointer"
                            onClick={() => windowOpenNewTab(`/whatsapp-chatbot-templates/${data.category}/${data.title}/${data.id}`)}
                        >
                            <div className="relative">
                                <Image src={data.picture} alt={data.title} width={280} height={170} style={{ height: "200px" }} className="w-full" />

                                <div
                                    className="absolute top-0 left-0 w-full h-full"
                                    style={{
                                        background: "linear-gradient(180deg, rgba(51, 100, 250, 0.00) 0%, rgba(51, 100, 250, 0.12) 100%)",
                                    }}
                                ></div>
                            </div>
                            <div className="p-8 bg-white">
                                {data.tags?.length > 0 && (
                                    <p className="mb-4 text-gray-700 text-sm">{data.tags.map((tag: { name: string }) => tag.name.toUpperCase()).join(" / ")}</p>
                                )}
                                <div className="h-24">
                                    <p className="mb-12 text-2xl font-medium">{data.title}</p>
                                </div>
                                <div className="flex gap-x-4 items-center">
                                    <a
                                        href={`/try-template/${data.id}`}
                                        className="border border-solid border-blue-600 text-blue-600 rounded no-underline hover:no-underline text-sm"
                                        style={{ lineHeight: "14px", padding: "8px 12px 8px 12px" }}
                                    >
                                        TRY
                                    </a>
                                    <div className="flex gap-1 items-center">
                                        <Image src="/new-industries/education/view.svg" width={20} height={20} className="w-8 h-6" alt="visited" />
                                        {data.usedCount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mt-9 text-blue-500 flex justify-center">
                    <a href="https://gallabox.com/whatsapp-chatbot-templates/education" className="flex items-center gap-x-2">
                        View all <MdOutlineArrowForwardIos size={14} />
                    </a>
                </p>
            </section>

            <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Elevate your customers’ digital shopping experience. From chats to checkouts." description="Take Gallabox for a 7-day ride" />
        </NewLayout>
    );
};

export default EcommerceLanding;

export const getStaticProps: GetStaticProps = async () => {
    let categoryResult;

    try {
        await axios({
            method: "get",
            url: "https://server.gallabox.com/devapi/bot-templates",
            headers: {
                apiSecret: process.env.REACT_APP_API_SECRET,
                apiKey: process.env.REACT_APP_API_KEY,
            },
            data: {
                status: "published",
                category: "ecommerce",
            },
        })
            .then(function (response) {
                categoryResult = JSON.parse(JSON.stringify(response?.data)) || [];
            })
            .catch(function (err) {
                console.log(err);
            });
    } catch (error) {
        throw new Error("Index creation failed.");
    }

    return {
        props: { categoryResult },
        ...(processEnv.isr.enable && { revalidate: 1 }),
    };
};
